import { FC, useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../redux/hooks'
import { useTranslation } from '../../../../hooks/locale'
import { getCurrentOpenedFilterModal, popModal, pushModal } from '../../../../redux/filters'
import { FilterModalType } from '../../filter-sort-section/types'
import { useSmartSearch } from '../hooks'
import SmartSearchField from '../smart-search-field'
import ActionSheet from '../../action-sheet'
import {
    CloseButton,
    CloseButtonIcon,
    ContentWrapper,
    Divider,
    FooterWrapper,
    HeaderRow,
    HeaderTitle,
    SparkleIcon,
    SubmitButton,
    SuggestionChip,
    SuggestionList,
    SuggestionsLabel,
} from './styles'

const noop = () => {}

type Props = {
    handleModalToggle: () => void
}

const SmartSearchModal: FC<Props> = ({ handleModalToggle }) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const currentOpenedFilterModal = useSelector(getCurrentOpenedFilterModal)
    const isOpen = currentOpenedFilterModal === FilterModalType.SMART_SEARCH

    const openedModalRef = useRef(currentOpenedFilterModal)

    useEffect(() => {
        openedModalRef.current = currentOpenedFilterModal
    }, [currentOpenedFilterModal])

    const onError = useCallback(() => {
        if (!openedModalRef.current) {
            dispatch(pushModal(FilterModalType.SMART_SEARCH))
        }
    }, [dispatch])

    const { text, setText, hasError, canSubmit, submit } = useSmartSearch({ onSuccess: noop, onError })

    const suggestions = [
        t('smartSearch.suggestionDogFriendly'),
        t('smartSearch.suggestionDishwasherWifi'),
        t('smartSearch.suggestionBeach'),
        t('smartSearch.suggestionSauna'),
    ]

    const handleSubmit = useCallback(() => {
        dispatch(popModal())
        submit()
    }, [dispatch, submit])

    return (
        <ActionSheet
            isOpen={isOpen}
            onRequestClose={handleModalToggle}
            header={
                <HeaderRow>
                    <SparkleIcon />
                    <HeaderTitle>{t('smartSearch.sheetTitle')}</HeaderTitle>
                    <CloseButton type="button" onClick={handleModalToggle} aria-label={t('smartSearch.closeLabel')}>
                        <CloseButtonIcon />
                    </CloseButton>
                </HeaderRow>
            }
            footer={
                <FooterWrapper>
                    <SubmitButton type="button" onClick={handleSubmit} disabled={!canSubmit}>
                        {t('smartSearch.mobileSubmit')}
                    </SubmitButton>
                </FooterWrapper>
            }
        >
            <ContentWrapper>
                <SmartSearchField value={text} onChange={setText} hasError={hasError} variant="mobile" />

                <Divider />

                <SuggestionsLabel>{t('smartSearch.suggestionsLabel')}</SuggestionsLabel>

                <SuggestionList>
                    {suggestions.map(suggestion => (
                        <SuggestionChip key={suggestion} type="button" onClick={() => setText(suggestion)}>
                            {suggestion}
                        </SuggestionChip>
                    ))}
                </SuggestionList>
            </ContentWrapper>
        </ActionSheet>
    )
}

export default SmartSearchModal
